import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "@/auth/AuthContext";
import { toast } from "sonner";

const GOLD = "#c9a86a";
const TOKEN_KEY = "wedflow_admin_token";

const STATUS_COLORS = {
  pending: "#d97706",
  onboarding: "#2563eb",
  active: "#16a34a",
  suspended: "#dc2626",
};

function call(action, token, extra = {}) {
  return supabase.functions.invoke("partner-admin", {
    body: { action, ...extra },
    headers: { "x-admin-token": token },
  });
}

export default function AdminPartner() {
  const [token, setToken] = useState(
    () => localStorage.getItem(TOKEN_KEY) || ""
  );
  const [authed, setAuthed] = useState(false);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form nuovo partner
  const [form, setForm] = useState({
    type: "supplier",
    business_name: "",
    contact_name: "",
    email: "",
    tier: "standard",
  });
  const [result, setResult] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [cardLinks, setCardLinks] = useState({}); // { [partner_id]: { loading, url } }
  const [linkingId, setLinkingId] = useState(null); // partner_id with open link form
  const [linkEmail, setLinkEmail] = useState("");
  const [linkingLoading, setLinkingLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await call("list", token);
    if (error || data?.error) {
      if (error?.message?.includes("401") || data?.error === "unauthorized") {
        toast.error("Token non valido");
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
      } else {
        toast.error("Errore nel caricamento");
      }
      setLoading(false);
      return;
    }
    setPartners(data?.partners || []);
    setAuthed(true);
    localStorage.setItem(TOKEN_KEY, token);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) load();
  }, [load]);

  const onLogin = (e) => {
    e.preventDefault();
    if (!token.trim()) return toast.error("Inserisci il token");
    load();
  };

  const onGenerateLink = async (partner) => {
    const action = partner.type === "supplier" ? "onboarding_link" : "venue_checkout_link";
    setCardLinks(prev => ({ ...prev, [partner.id]: { loading: true, url: null } }));
    try {
      const { data, error } = await call(action, token, { partner_id: partner.id });
      if (error || data?.error) {
        toast.error(data?.error || "Errore nella generazione del link");
        setCardLinks(prev => ({ ...prev, [partner.id]: { loading: false, url: null } }));
        return;
      }
      const url = data?.onboarding_url || data?.checkout_url || "";
      setCardLinks(prev => ({ ...prev, [partner.id]: { loading: false, url } }));
    } catch {
      setCardLinks(prev => ({ ...prev, [partner.id]: { loading: false, url: null } }));
    }
  };

  const onDelete = async (partner) => {
    if (!window.confirm(`Eliminare "${partner.business_name}"? L'operazione è irreversibile.`)) return;
    setDeletingId(partner.id);
    try {
      const { data, error } = await call("delete", token, { partner_id: partner.id });
      if (error || data?.error) {
        toast.error(data?.error || "Errore nell'eliminazione");
        return;
      }
      toast.success(`${partner.business_name} eliminato`);
      await load();
    } finally {
      setDeletingId(null);
    }
  };

  const onLinkUser = async (partnerId) => {
    if (!linkEmail.trim()) return toast.error("Inserisci l'email dell'utente");
    setLinkingLoading(true);
    try {
      const { data, error } = await call("link_venue_user", token, {
        partner_id: partnerId,
        user_email: linkEmail.trim(),
      });
      if (error || data?.error) {
        const msg = data?.error === "user_not_found"
          ? "Nessun account WedFlow trovato con questa email"
          : (data?.error || "Errore nel collegamento");
        toast.error(msg);
        return;
      }
      toast.success("Utente collegato");
      setLinkingId(null);
      setLinkEmail("");
      await load();
    } finally {
      setLinkingLoading(false);
    }
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setResult(null);
    try {
      const action = form.type === "supplier" ? "create_supplier" : "create_venue";
      const { data, error } = await call(action, token, form);
      if (error || data?.error) {
        toast.error(data?.error || "Errore nella creazione");
        return;
      }
      setResult(data);
      toast.success("Partner creato");
      await load();
    } finally {
      setCreating(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-5">
        <form onSubmit={onLogin}
          className="w-full max-w-md border border-neutral-200 rounded-[14px] p-8 md:p-10 bg-white">
          <div className="flex items-center gap-2 mb-8">
            <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />
            <span className="font-display tracking-[0.28em] text-sm font-medium text-neutral-950">
              WEDFLOW · PARTNER ADMIN
            </span>
          </div>
          <h1 className="font-display text-[28px] tracking-[-0.02em] font-light text-neutral-950">
            Accesso riservato
          </h1>
          <label className="mt-8 block text-[11px] tracking-[0.22em] uppercase text-neutral-500">
            Admin token
          </label>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="••••••••••••"
            className="mt-2 w-full bg-white border border-neutral-200 rounded-full px-5 py-3.5 text-[14px] outline-none focus:border-neutral-400 transition"
            autoComplete="off"
            autoFocus
          />
          <button type="submit" disabled={loading}
            className="mt-6 w-full wf-btn-primary rounded-full py-3.5 text-[13px] tracking-wide disabled:opacity-60">
            {loading ? "Verifica…" : "Accedi"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-[1240px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />
            <span className="font-display tracking-[0.28em] text-[13px] font-medium text-neutral-950">
              WEDFLOW · PARTNER ADMIN
            </span>
          </div>
          <div className="flex gap-2">
            <button onClick={load}
              className="border border-neutral-200 rounded-full px-4 py-2 text-[12px] tracking-wide hover:bg-neutral-50 transition">
              Aggiorna
            </button>
            <button onClick={() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); }}
              className="border border-neutral-200 rounded-full px-4 py-2 text-[12px] tracking-wide hover:bg-neutral-50 transition">
              Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1240px] mx-auto px-5 md:px-8 py-10 md:py-16 grid grid-cols-1 lg:grid-cols-2 gap-12">

        {/* Form crea partner */}
        <section>
          <h2 className="font-display text-[26px] tracking-[-0.02em] font-light text-neutral-950 mb-8">
            Nuovo partner
          </h2>
          <form onSubmit={onCreate} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1.5 text-[11px] tracking-[0.18em] uppercase text-neutral-500">
              Tipo
              <select value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className="bg-white border border-neutral-200 rounded-[10px] px-4 py-3 text-[14px] outline-none focus:border-neutral-400 transition text-neutral-800 normal-case tracking-normal">
                <option value="supplier">Fornitore (referral + Stripe Connect)</option>
                <option value="venue">Location (abbonamento annuale)</option>
              </select>
            </label>

            {form.type === "venue" && (
              <label className="flex flex-col gap-1.5 text-[11px] tracking-[0.18em] uppercase text-neutral-500">
                Tier
                <select value={form.tier}
                  onChange={e => setForm(f => ({ ...f, tier: e.target.value }))}
                  className="bg-white border border-neutral-200 rounded-[10px] px-4 py-3 text-[14px] outline-none focus:border-neutral-400 transition text-neutral-800 normal-case tracking-normal">
                  <option value="founding">Founding — €890/anno (primo anno)</option>
                  <option value="standard">Standard — €1.190/anno</option>
                  <option value="unlimited">Unlimited — €1.799/anno</option>
                </select>
              </label>
            )}

            <input required placeholder="Nome attività *" value={form.business_name}
              onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
              className="bg-white border border-neutral-200 rounded-[10px] px-4 py-3 text-[14px] outline-none focus:border-neutral-400 transition" />
            <input required placeholder="Nome referente *" value={form.contact_name}
              onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
              className="bg-white border border-neutral-200 rounded-[10px] px-4 py-3 text-[14px] outline-none focus:border-neutral-400 transition" />
            <input required type="email" placeholder="Email *" value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="bg-white border border-neutral-200 rounded-[10px] px-4 py-3 text-[14px] outline-none focus:border-neutral-400 transition" />

            <button type="submit" disabled={creating}
              className="wf-btn-primary rounded-full py-3.5 text-[13px] tracking-wide disabled:opacity-50">
              {creating ? "Creazione…" : form.type === "supplier" ? "Crea fornitore e genera link onboarding →" : "Crea location e genera link pagamento →"}
            </button>
          </form>

          {/* Risultato creazione */}
          {result && (
            <div className="mt-6 rounded-[12px] border border-neutral-200 p-6 bg-neutral-50">
              <div className="text-[11px] tracking-[0.2em] uppercase text-neutral-500 mb-3">
                {result.onboarding_url ? "Link onboarding Stripe" : "Link checkout abbonamento"}
              </div>
              <div className="flex items-start gap-3">
                <input readOnly
                  value={result.onboarding_url || result.checkout_url || ""}
                  className="flex-1 bg-white border border-neutral-200 rounded-[10px] px-4 py-2.5 text-[12px] outline-none font-mono break-all"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(result.onboarding_url || result.checkout_url || "");
                    toast.success("Copiato");
                  }}
                  className="border border-neutral-200 rounded-[10px] px-4 py-2.5 text-[12px] hover:bg-white transition whitespace-nowrap">
                  Copia
                </button>
              </div>
              <p className="mt-3 text-[12px] text-neutral-500">
                {result.onboarding_url
                  ? "Invia questo link al fornitore. Scadrà dopo ~10 minuti."
                  : "Invia questo link alla location per completare il pagamento."}
              </p>
            </div>
          )}
        </section>

        {/* Lista partner */}
        <section>
          <h2 className="font-display text-[26px] tracking-[-0.02em] font-light text-neutral-950 mb-8">
            Partner ({partners.length})
          </h2>
          {loading ? (
            <div className="text-[14px] text-neutral-500">Caricamento…</div>
          ) : partners.length === 0 ? (
            <div className="text-[14px] text-neutral-500">Nessun partner ancora.</div>
          ) : (
            <div className="flex flex-col gap-3">
              {partners.map(p => (
                <div key={p.id} className="border border-neutral-200 rounded-[12px] p-5 bg-white">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium text-[14px] text-neutral-950">{p.business_name}</div>
                      <div className="text-[12px] text-neutral-500 mt-0.5">{p.email}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                      <span className="text-[10px] tracking-[0.14em] uppercase text-neutral-500">
                        {p.type}{p.tier ? ` · ${p.tier}` : ""}
                      </span>
                      <span className="rounded-full px-2.5 py-0.5 text-[10px] tracking-wide text-white font-medium"
                        style={{ background: STATUS_COLORS[p.status] || "#6b7280" }}>
                        {p.status}
                      </span>
                      {p.type === "venue" && p.status === "active" && p.user_linked === false && (
                        <span className="rounded-full px-2.5 py-0.5 text-[10px] tracking-wide text-white font-medium"
                          style={{ background: "#d97706" }}>
                          utente non collegato
                        </span>
                      )}
                    </div>
                  </div>
                  {p.promotion_code && (
                    <div className="mt-2 text-[12px] text-neutral-600">
                      Codice: <code className="font-mono bg-neutral-100 px-1.5 py-0.5 rounded">{p.promotion_code}</code>
                    </div>
                  )}

                  {/* Link onboarding / pagamento inline */}
                  {cardLinks[p.id]?.url && (
                    <div className="mt-3 rounded-[10px] border border-neutral-200 p-3 bg-neutral-50">
                      <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-400 mb-2">
                        {p.type === "supplier" ? "Link onboarding Stripe" : "Link checkout abbonamento"}
                      </div>
                      <div className="flex items-center gap-2">
                        <input readOnly value={cardLinks[p.id].url}
                          className="flex-1 bg-white border border-neutral-200 rounded-[8px] px-3 py-1.5 text-[11px] outline-none font-mono truncate" />
                        <button
                          onClick={() => { navigator.clipboard.writeText(cardLinks[p.id].url); toast.success("Copiato"); }}
                          className="border border-neutral-200 rounded-[8px] px-3 py-1.5 text-[11px] hover:bg-white transition whitespace-nowrap">
                          Copia
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Collegamento manuale utente — solo venue active non collegate */}
                  {p.type === "venue" && p.status === "active" && p.user_linked === false && (
                    <div className="mt-3">
                      {linkingId === p.id ? (
                        <div className="rounded-[10px] border border-amber-200 bg-amber-50 p-3">
                          <p className="text-[11px] text-amber-800 mb-2">
                            Inserisci l'email dell'account WedFlow da collegare:
                          </p>
                          <div className="flex gap-2">
                            <input
                              type="email"
                              value={linkEmail}
                              onChange={e => setLinkEmail(e.target.value)}
                              placeholder="email@esempio.it"
                              className="flex-1 bg-white border border-amber-200 rounded-[8px] px-3 py-1.5 text-[12px] outline-none focus:border-amber-400 transition"
                              onKeyDown={e => e.key === "Enter" && onLinkUser(p.id)}
                              autoFocus
                            />
                            <button
                              onClick={() => onLinkUser(p.id)}
                              disabled={linkingLoading}
                              className="bg-amber-600 text-white rounded-[8px] px-3 py-1.5 text-[11px] hover:bg-amber-700 transition disabled:opacity-50 whitespace-nowrap">
                              {linkingLoading ? "…" : "Collega"}
                            </button>
                            <button
                              onClick={() => { setLinkingId(null); setLinkEmail(""); }}
                              className="border border-neutral-200 rounded-[8px] px-3 py-1.5 text-[11px] hover:bg-neutral-50 transition">
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setLinkingId(p.id); setLinkEmail(""); }}
                          className="border border-amber-300 text-amber-700 rounded-full px-3 py-1 text-[11px] hover:bg-amber-50 transition">
                          Collega utente manualmente
                        </button>
                      )}
                    </div>
                  )}

                  <div className="mt-3 flex items-center justify-between gap-2">
                    <button
                      onClick={() => onGenerateLink(p)}
                      disabled={cardLinks[p.id]?.loading}
                      className="border border-neutral-200 rounded-full px-3 py-1 text-[11px] hover:bg-neutral-50 transition disabled:opacity-50">
                      {cardLinks[p.id]?.loading
                        ? "Generazione…"
                        : p.type === "supplier" ? "Genera link onboarding" : "Genera link pagamento"}
                    </button>
                    <button
                      onClick={() => onDelete(p)}
                      disabled={deletingId === p.id}
                      className="border border-red-200 text-red-600 rounded-full px-3 py-1 text-[11px] hover:bg-red-50 transition disabled:opacity-50">
                      {deletingId === p.id ? "Eliminazione…" : "Elimina"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
